import dimensionsExample from "../../assets/dimensions.png";
import { useContext, useEffect, useMemo, useReducer, useState } from "react";
import DimensionForm from "./dimensionForm";
import AppContext from "../../context";
import { IMPERIAL_ACTIONS, imperialReducer } from "../../util/hooks/dimensions";
import {
  cmToIn,
  decimalInchesToNearest16thFraction,
  fetchTemplateFromFirebase,
  notify,
} from "../../util/helpers";
import Modal from "../modal";
import { templateMapper } from "../../content/templates";
import Loader from "../loader";

const InitModal = ({ isOpen, onClose }) => {
  const {
    handleSetGlobalModal,
    MIN_HEIGHT,
    MIN_WIDTH_DEPTH,
    handleSetCurrentInsertDimensions,
    triggerPriceUpdate,
    triggerDimensionsUpdate,
  } = useContext(AppContext);

  const [imperial, setImperial] = useState(true);
  const [width, setWidth] = useState(50.8);
  const [depth, setDepth] = useState(40.64);
  const [height, setHeight] = useState(6.35);
  const [widthError, setWidthError] = useState(false);
  const [depthError, setDepthError] = useState(false);
  const [heightError, setHeightError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentDimensions, setCurrentDimensions] = useState(null);
  const [templatesModalOpen, setTemplatesModalOpen] = useState(false);

  const [_, dispatch] = useReducer(imperialReducer);

  const handleSetWidthError = (bool) => {
    setWidthError(bool);
  };
  const handleSetDepthError = (bool) => {
    setDepthError(bool);
  };
  const handleSetHeightError = (bool) => {
    setHeightError(bool);
  };
  const handleSetImperial = (bool) => {
    setImperial(bool);
  };

  const initializeDesigner = () => {
    // when the user clicks start designing, we need to adjust the dimensions
    // to be 1/8" smaller than the input dimensions
    // this is because the input dimensions are the inside dimensions of the drawer
    // and we need ensure a perfect fit
    const newWidthCm = width - 0.3;
    const newDepthCm = depth - 0.3;
    const newHeightCm = height > 7.9375 ? 7.62 : height - 0.3;
    const adjustedDimensions = {
      width: {
        cm: newWidthCm,
        in: decimalInchesToNearest16thFraction(cmToIn(newWidthCm)),
      },
      height: {
        cm: newHeightCm,
        in: decimalInchesToNearest16thFraction(cmToIn(newHeightCm)),
      },
      depth: {
        cm: newDepthCm,
        in: decimalInchesToNearest16thFraction(cmToIn(newDepthCm)),
      },
    };
    handleSetCurrentInsertDimensions(adjustedDimensions);
  };

  const templates = useMemo(() => {
    const foundEntry = Object.entries(templateMapper).find(([key, value]) => {
      const minWidth = key.split("_")[0];
      const maxWidth = key.split("_")[1];
      if (width >= minWidth && width <= maxWidth) {
        return true;
      }
      return false;
    });

    return foundEntry ? { ...foundEntry[1] } : null;
  }, [width]);

  useEffect(() => {
    const dimensionsObject = {
      width: {
        cm: width,
        in: decimalInchesToNearest16thFraction(cmToIn(width)),
      },
      height: {
        cm: height,
        in: decimalInchesToNearest16thFraction(cmToIn(height)),
      },
      depth: {
        cm: depth,
        in: decimalInchesToNearest16thFraction(cmToIn(depth)),
      },
    };
    setCurrentDimensions(dimensionsObject);
  }, [imperial, width, depth, height]);

  const handleTemplateSelect = async (storageLocation) => {
    // Close template modal
    setTemplatesModalOpen(false);
    // Initialize designer with the selected template
    //handleSetCurrentInsertDimensions(currentDimensions);

    // Load the template from Firebase
    setLoading(true);
    const template = await fetchTemplateFromFirebase(storageLocation);

    if (template.error) {
      return;
    } else if (window?.loadInsert) {
      window.loadInsert(JSON.stringify(template));
    }
    setTimeout(() => {
      setLoading(false);
      triggerPriceUpdate();
      triggerDimensionsUpdate();
      notify("Template loaded!");
    }, 250);
    // Close the entire modal
    if (handleSetGlobalModal) {
      handleSetGlobalModal(null);
    }
  };

  return (
    <div className="common-modal">
      <div
        className="common-modal__content content p-5"
        style={{ overflowY: "hidden" }}
      >
        {loading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: "100",
              backgroundColor: "rgba(255,255,255,0.8)",
            }}
          >
            <Loader />
          </div>
        )}
        <h2 className="title is-4 has-text-centered">
          WHAT SIZE DRAWER ARE YOU TRYING TO ORGANIZE?
        </h2>

        <p className="mb-4 has-text-centered">
          Enter the inside measurements of your drawer and we will make your
          organizer 1/8" (3mm) smaller to ensure a perfect fit.
        </p>

        {/* Add dimensions image */}
        <div
          className="text-center mb-4"
          style={{ display: "flex", justifyContent: "center" }}
        >
          <img
            src={dimensionsExample}
            alt="Drawer dimensions diagram"
            width="450"
            height="325"
            style={{ maxWidth: "100%", height: "auto" }}
          />
        </div>

        <DimensionForm
          imperial={imperial}
          widthError={widthError}
          depthError={depthError}
          heightError={heightError}
          handleSetImperial={handleSetImperial}
          handleSetWidthError={handleSetWidthError}
          handleSetDepthError={handleSetDepthError}
          handleSetHeightError={handleSetHeightError}
          width={width}
          depth={depth}
          height={height}
          setWidth={setWidth}
          setDepth={setDepth}
          setHeight={setHeight}
          currentDimensions={currentDimensions}
          setCurrentDimensions={setCurrentDimensions}
        />

        <div className="buttons is-centered mt-5">
          <button
            className="button is-primary is-medium mb-0"
            onClick={() => {
              // initialize the designer with the current dimensions
              initializeDesigner();
              // Close the modal and continue to the designer
              if (handleSetGlobalModal) {
                handleSetGlobalModal(null);
              }
            }}
            disabled={widthError || depthError || heightError}
          >
            Start Designing
          </button>
          <p className="is-size-5 mb-0">{"- or -"}</p>
          {templates && templates.items && (
            <button
              className="button is-primary is-medium mb-0"
              onClick={() => {
                setTemplatesModalOpen(!templatesModalOpen);
              }}
              disabled={widthError || depthError || heightError}
            >
              Choose a Template
            </button>
          )}
        </div>
      </div>
      <Modal trigger={templatesModalOpen} options={{ wide: true }}>
        <div className="common-modal">
          <div className="common-modal__content p-5" style={{ width: "100%" }}>
            <h2 className="title is-4 has-text-centered">
              {templates?.name} Templates
            </h2>
            <p className="mb-4 has-text-centered">
              Select a template as a starting point for your custom wood drawer
              organizer. You will need to fine tune the size and spacing to fit
              your measurements.
            </p>

            {templates && templates.items && (
              <div
                className="template-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gridTemplateRows: "repeat(2, auto)",
                  gap: "1.5rem",
                }}
              >
                {templates.items.map((template, index) => (
                  <div
                    key={index}
                    className="template-item"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      handleTemplateSelect(template.storageLocation)
                    }
                    onMouseEnter={(e) => {
                      const imgElement = e.currentTarget.querySelector("img");
                      if (imgElement && template.imageSide) {
                        imgElement.src =
                          require(`../../assets/templates/${template.imageSide}`).default;
                      }
                    }}
                    onMouseLeave={(e) => {
                      const imgElement = e.currentTarget.querySelector("img");
                      if (imgElement) {
                        imgElement.src =
                          require(`../../assets/templates/${template.image}`).default;
                      }
                    }}
                  >
                    <div
                      className="template-image"
                      style={{
                        position: "relative",
                        height: "300px" /* Fixed height for image container */,
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src={
                          require(`../../assets/templates/${template.image}`)
                            .default
                        }
                        alt={template.name}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          borderRadius: "4px",
                          objectFit: "contain",
                          transition: "opacity 0.3s ease",
                        }}
                      />
                    </div>
                    <div
                      className="template-name has-text-centered"
                      style={{ minHeight: "40px" }}
                    >
                      <strong>{template.name}</strong>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {templates && !templates.items && (
              <div className="notification is-warning">
                No templates available for the current drawer dimensions.
              </div>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default InitModal;
